import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  DialogContent,
  TextField,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useNotify } from '@/hooks/useNotify';
import { useUpdateTask, type TaskListItem } from '@/hooks/useTasks';
import { useStaffUsers } from '@/hooks/useStaffUsers';
import { sortStaffByDisplayName } from '@/utils/staffNames';
import { useWorkOrders, type WorkOrderListItem } from '@/hooks/useWorkOrders';
import { TaskAudienceField } from './TaskAudienceField';
import { type TaskAudience } from './taskAudience';

interface EditTaskDialogProps {
  open: boolean;
  onClose: () => void;
  task: TaskListItem | null;
}

const priorities = ['low', 'normal', 'high', 'urgent'] as const;

export function EditTaskDialog({ open, onClose, task }: EditTaskDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateTask = useUpdateTask();
  const { isDirty, markDirty } = useFormDirty(open);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [priority, setPriority] = useState<string>('normal');
  const [audience, setAudience] = useState<TaskAudience>('anyone');
  const [assignedToId, setAssignedToId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [estimatedDuration, setEstimatedDuration] = useState<string>('');
  const [deadline, setDeadline] = useState<Date | null>(null);

  const staffUsersQuery = useStaffUsers({ isActive: true });
  const workOrdersQuery = useWorkOrders({ limit: 50 });

  const staffUsers = sortStaffByDisplayName(staffUsersQuery.data?.users ?? []);
  const workOrders = workOrdersQuery.data?.pages.flatMap((p) => p.data) ?? [];

  // Hydrate form from the selected task whenever the dialog opens for a task.
  useEffect(() => {
    if (open && task) {
      setTitle(task.title);
      setDescription(task.description ?? '');
      setNote(task.note ?? '');
      setPriority(task.priority);
      setAudience(task.audience ?? 'anyone');
      setAssignedToId(task.assignedTo?.id ?? null);
      setBookingId(task.booking?.id ?? null);
      setScheduledAt(task.scheduledAt ? new Date(task.scheduledAt) : null);
      setEstimatedDuration(
        task.estimatedDurationHours != null ? String(task.estimatedDurationHours) : '',
      );
      setDeadline(task.deadline ? new Date(task.deadline) : null);
    }
  }, [open, task]);

  const handleSubmit = async () => {
    if (!task || !title.trim()) return;

    try {
      await updateTask.mutateAsync({
        id: task.id,
        data: {
          title: title.trim(),
          description: description.trim() || undefined,
          note: note.trim() || null,
          priority: priority as 'low' | 'normal' | 'high' | 'urgent',
          audience,
          assignedToId: assignedToId || null,
          bookingId: bookingId || null,
          scheduledAt: scheduledAt ? scheduledAt.toISOString() : null,
          estimatedDurationHours: estimatedDuration ? parseInt(estimatedDuration, 10) : null,
          deadline: deadline ? deadline.toISOString() : null,
        },
      });
      notify.success(t('myTasks.updated'));
      onClose();
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: t('myTasks.actionFailed') }));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('myTasks.editTask')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      submitDisabled={!title.trim()}
      isLoading={updateTask.isPending}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('myTasks.taskTitle')}
            value={title}
            onChange={(e) => { setTitle(e.target.value); markDirty(); }}
            required
            autoFocus
            fullWidth
          />

          <TextField
            label={t('myTasks.taskDescription')}
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            multiline
            rows={3}
            fullWidth
          />

          <TextField
            label={t('myTasks.taskNote')}
            placeholder={t('myTasks.taskNotePlaceholder')}
            value={note}
            onChange={(e) => { setNote(e.target.value); markDirty(); }}
            multiline
            rows={2}
            fullWidth
          />

          <TextField
            select
            label={t('myTasks.priority')}
            value={priority}
            onChange={(e) => { setPriority(e.target.value); markDirty(); }}
            fullWidth
          >
            {priorities.map((p) => (
              <MenuItem key={p} value={p}>
                {t(`myTasks.priority${p.charAt(0).toUpperCase() + p.slice(1)}`)}
              </MenuItem>
            ))}
          </TextField>

          <TaskAudienceField
            value={audience}
            onChange={(val) => { setAudience(val); markDirty(); }}
          />

          <Autocomplete
            options={staffUsers}
            getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
            value={staffUsers.find((u) => u.id === assignedToId) ?? null}
            onChange={(_, val) => { setAssignedToId(val?.id ?? null); markDirty(); }}
            renderInput={(params) => (
              <TextField {...params} label={t('myTasks.assignTo')} />
            )}
          />

          <Autocomplete
            options={workOrders}
            getOptionLabel={(wo: WorkOrderListItem) =>
              `${wo.vehicleRegNumber} — ${wo.serviceType}`
            }
            value={workOrders.find((wo) => wo.id === bookingId) ?? null}
            onChange={(_, val) => { setBookingId(val?.id ?? null); markDirty(); }}
            renderInput={(params) => (
              <TextField {...params} label={t('myTasks.selectWorkOrder')} />
            )}
          />

          <DateTimePicker
            label={t('myTasks.scheduledAt')}
            value={scheduledAt}
            onChange={(val: Date | null) => { setScheduledAt(val); markDirty(); }}
            ampm={false}
            slotProps={{ textField: { fullWidth: true }, openPickerIcon: { fontSize: 'small' } }}
          />

          <TextField
            type="number"
            label={t('myTasks.estimatedDuration')}
            value={estimatedDuration}
            onChange={(e) => { setEstimatedDuration(e.target.value); markDirty(); }}
            slotProps={{ htmlInput: { min: 0 } }}
            fullWidth
          />

          <DateTimePicker
            label={t('myTasks.deadline')}
            value={deadline}
            onChange={(val: Date | null) => { setDeadline(val); markDirty(); }}
            ampm={false}
            slotProps={{ textField: { fullWidth: true }, openPickerIcon: { fontSize: 'small' } }}
          />
        </Box>
      </DialogContent>
    </FormDialog>
  );
}
